import React, { createContext, useContext, useState, useCallback } from 'react'
import { Snackbar, SnackbarType } from '../components/Snackbar'

interface SnackbarMessage {
  id: string
  message: string
  type: SnackbarType
}

interface SnackbarContextType {
  showSnackbar: (message: string, type: SnackbarType) => void
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([])

  const showSnackbar = useCallback((message: string, type: SnackbarType) => {
    const id = Math.random().toString(36).substring(7)
    setSnackbars((prev) => [...prev, { id, message, type }])
  }, [])

  const removeSnackbar = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id))
  }, [])

  const success = useCallback((message: string) => showSnackbar(message, 'success'), [showSnackbar])
  const error = useCallback((message: string) => showSnackbar(message, 'error'), [showSnackbar])
  const warning = useCallback((message: string) => showSnackbar(message, 'warning'), [showSnackbar])
  const info = useCallback((message: string) => showSnackbar(message, 'info'), [showSnackbar])

  return (
    <SnackbarContext.Provider value={{ showSnackbar, success, error, warning, info }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        {snackbars.map((snackbar) => (
          <div key={snackbar.id} className="pointer-events-auto">
            <Snackbar
              message={snackbar.message}
              type={snackbar.type}
              onClose={() => removeSnackbar(snackbar.id)}
            />
          </div>
        ))}
      </div>
    </SnackbarContext.Provider>
  )
}

export function useSnackbar() {
  const context = useContext(SnackbarContext)
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}
